import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const BudgetVarianceAnalysis = ({ projects, loading }) => {
  const [expanded, setExpanded] = useState(null);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="animate-pulse space-y-3">
          {[1,2,3]?.map(i => <div key={i} className="h-12 bg-gray-100 rounded" />)}
        </div>
      </div>
    );
  }

  const projectsWithBudget = (projects ?? [])?.filter(p => p?.budget || p?.contractValue);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <Icon name="BarChart2" size={16} className="text-indigo-600" />
          Budget Variance Analysis
        </h3>
      </div>
      <div className="divide-y divide-gray-50">
        {projectsWithBudget?.length === 0 ? (
          <div className="px-5 py-8 text-center text-gray-400 text-sm">
            No budget data available
          </div>
        ) : (
          projectsWithBudget?.map((project, idx) => {
            const budget = parseFloat(project?.budget ?? project?.contractValue ?? 0);
            const actual = parseFloat(project?.actualCost ?? 0);
            const variance = budget > 0 ? ((actual - budget) / budget) * 100 : 0;
            const isOver = variance > 0;
            const isExpanded = expanded === project?.id;

            return (
              <div key={project?.id ?? idx}>
                <div
                  className="px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpanded(isExpanded ? null : project?.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon
                        name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
                        size={14}
                        className="text-gray-400 flex-shrink-0"
                      />
                      <span className="text-sm font-medium text-gray-800 truncate">{project?.name}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`text-xs font-semibold flex items-center gap-1 ${
                        isOver ? 'text-red-600' : 'text-green-600'
                      }`}>
                        <Icon name={isOver ? 'TrendingUp' : 'TrendingDown'} size={12} />
                        {isOver ? '+' : ''}{variance?.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className="ml-5 mt-2">
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>Budget: ${budget?.toLocaleString()}</span>
                      <span>Actual: ${actual?.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full ${isOver ? 'bg-red-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(Math.abs(variance) * 2, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <div className="px-5 pb-3 ml-5 bg-gray-50">
                    <div className="grid grid-cols-2 gap-3 pt-2">
                      <div className="bg-white rounded border border-gray-100 p-3">
                        <p className="text-xs text-gray-400 mb-1">Estimated Budget</p>
                        <p className="text-sm font-semibold text-gray-700">${budget?.toLocaleString()}</p>
                      </div>
                      <div className="bg-white rounded border border-gray-100 p-3">
                        <p className="text-xs text-gray-400 mb-1">Actual Cost</p>
                        <p className={`text-sm font-semibold ${isOver ? 'text-red-600' : 'text-green-600'}`}>
                          ${actual?.toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-white rounded border border-gray-100 p-3">
                        <p className="text-xs text-gray-400 mb-1">Variance Amount</p>
                        <p className={`text-sm font-semibold ${isOver ? 'text-red-600' : 'text-green-600'}`}>
                          {isOver ? '+' : ''}${(actual - budget)?.toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-white rounded border border-gray-100 p-3">
                        <p className="text-xs text-gray-400 mb-1">Status</p>
                        <p className={`text-sm font-semibold ${isOver ? 'text-red-600' : 'text-green-600'}`}>
                          {Math.abs(variance) < 5 ? 'On Budget' : isOver ? 'Over Budget' : 'Under Budget'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default BudgetVarianceAnalysis;
